import cx from 'classnames'
import {
  SyntheticEvent,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ChangeEvent, ElementType, KeyboardEvent, Ref } from 'react'
import { v4 as uuid } from 'uuid'

import { useOutsideClickDetect } from '@mondough/utils'

import type { TProps as TextInputProps } from '../text-input/text-input'
import styles from './autocomplete.module.scss'
import { DefaultSuggestionRenderer } from './suggestion-renderer'
import { Icon, LoadingSpinner, PlainCard, TextInput, VisuallyHidden } from '..'

type Suggestions<T> = { id: string } & T

export interface Props<Suggestions>
  extends Omit<TextInputProps, 'onBlur' | 'onChange' | 'type'> {
  clearOnSelect?: boolean
  hideSuggestionsOnSelect?: boolean
  loading?: boolean
  onBlur?: () => void
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  onSelection: (suggestionId: string, event: SyntheticEvent) => void
  SuggestionRenderer?: ElementType
  suggestions: Suggestions[]
  value?: string
  multiple?: boolean
  isOpenByDefault?: boolean
  selections?: string[]
  clearButtonRef?: Ref<HTMLButtonElement>
  showChevron?: boolean
}

export default forwardRef(function Autocomplete<T>(
  {
    id,
    clearOnSelect = true,
    disabled,
    fullWidth,
    hideSuggestionsOnSelect = true,
    invalid,
    loading = false,
    onChange,
    onBlur,
    onClear,
    onSelection,
    placeholder,
    required,
    SuggestionRenderer = DefaultSuggestionRenderer,
    suggestions,
    isOpenByDefault = true,
    multiple = false,
    selections,
    value,
    allowClearOnDisabled,
    clearButtonRef,
    showChevron,
    className,
    defaultValue,
  }: Props<Suggestions<T>>,
  inputRef: Ref<HTMLInputElement>,
) {
  const elementId = useMemo(() => uuid(), [])
  const rootRef = useRef<HTMLDivElement>(null)
  const [listIsOpen, setListIsOpen] = useState(isOpenByDefault)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const listIsVisible = suggestions.length > 0 && listIsOpen

  const closeList = useCallback(() => {
    setListIsOpen(false)
    if (listIsOpen) onBlur?.()
  }, [listIsOpen, onBlur])

  const openList = useCallback(() => setListIsOpen(true), [])

  // Hide the list when clicking outside the component, but retain the suggestions
  useOutsideClickDetect(rootRef, closeList)

  const handleSelection = (
    suggestion: Suggestions<T>,
    event: SyntheticEvent,
  ) => {
    if (clearOnSelect && !!onClear) {
      onClear()
      setSelectedIndex(0)
    }
    onSelection(suggestion.id, event)
    if (hideSuggestionsOnSelect) {
      closeList()
    }
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault()
        if (selectedIndex === 0) {
          setSelectedIndex(suggestions.length - 1)
          break
        }
        setSelectedIndex((prevIndex) => prevIndex - 1)
        break
      case 'ArrowDown':
        event.preventDefault()
        if (selectedIndex === suggestions.length - 1) {
          setSelectedIndex(0)
          break
        }
        setSelectedIndex((prevIndex) => prevIndex + 1)
        break
      case 'Enter':
        event.preventDefault()
        const selected = suggestions[selectedIndex] as Suggestions<T>
        if (selected != null) {
          handleSelection(selected, event)
        }
        break
      default:
        openList()
        return
    }
  }

  return (
    <div
      role="combobox"
      aria-expanded={listIsVisible}
      aria-controls={elementId}
      aria-haspopup="listbox"
      onFocus={openList}
      className={cx(styles.root, className, { [styles.fullWidth]: fullWidth })}
      onKeyDown={handleKeyDown}
      ref={rootRef}
    >
      <TextInput
        id={id}
        aria-label="autocomplete-input"
        aria-invalid={invalid}
        invalid={invalid}
        disabled={disabled}
        fullWidth={fullWidth}
        onChange={onChange}
        onClear={onClear}
        allowClearOnDisabled={allowClearOnDisabled}
        placeholder={placeholder}
        ref={inputRef}
        clearButtonRef={clearButtonRef}
        required={required}
        type="search"
        className={cx({ [styles.inputExpanded]: listIsVisible })}
        value={value}
        defaultValue={defaultValue}
      />
      {loading ? (
        <LoadingSpinner size={18} className={styles.loading} />
      ) : (
        showChevron && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => setListIsOpen((open) => !open)}
            className={cx(styles.chevron, {
              [styles.hasClearButton]: !!onClear,
            })}
          >
            <Icon
              size={18}
              icon={listIsOpen ? 'chevron-up' : 'chevron-down'}
              aria-hidden
            />
            <VisuallyHidden>
              {listIsOpen ? 'Close' : 'Open'} this field
            </VisuallyHidden>
          </button>
        )
      )}
      {listIsVisible && (
        <PlainCard
          as="ul"
          id={elementId}
          role="listbox"
          className={styles.suggestions}
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.id}
              role="button"
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={(e) => handleSelection(suggestion, e)}
              onFocus={() => setSelectedIndex(index)}
              tabIndex={0}
              className={cx(styles.suggestion, {
                [styles.selected]:
                  selectedIndex === index ||
                  (selections || [])?.includes(suggestion.id),
              })}
            >
              <SuggestionRenderer
                suggestion={suggestion}
                selected={
                  multiple && (selections || [])?.includes(suggestion.id)
                }
              />
            </li>
          ))}
        </PlainCard>
      )}
    </div>
  )
})
